import { Component, inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { Types } from '../../../domain/types.domain';
import { queryParam } from '../../signals';

import { AuthRepositoryInstruction } from '../../../builder/instructions/repositories/auth.repository.instructions';
import { GetPylonCitiesServiceInstruction } from '../../../builder/instructions/services/get-pylon-cities.service.instruction';

/**
 * Página de cold login do SSO OIDC. O backend (/oauth/authorize, sem sessão)
 * redireciona pra cá com `redirectTo` (=/oauth/session) e `returnTo` (o /authorize
 * canônico). Coleta o e-mail, resolve a cidade e entra no login normal repassando
 * esses params — o login-frontend já os propaga até o callback.
 */
@Component({
  selector: 'app-sso',
  standalone: true,
  imports: [ReactiveFormsModule],
  providers: [new AuthRepositoryInstruction(), new GetPylonCitiesServiceInstruction()],
  templateUrl: './sso.component.html',
  styleUrl: './sso.component.scss',
})
export class SsoComponent {
  private readonly _router = inject(Router);
  private readonly _getCitiesService = inject(Types.GetPylonCitiesService);

  private readonly redirectToParam = queryParam('redirectTo');
  private readonly returnToParam = queryParam('returnTo');

  public emailFormControl = new FormControl('', [Validators.required, Validators.email]);
  public cityFormControl = new FormControl('');

  public loading = signal(false);
  public errorMessage = signal('');
  public cities = signal<{ identity: string; name: string; state: string }[]>([]);

  async findCities(): Promise<void> {
    this.emailFormControl.markAsTouched();

    if (this.emailFormControl.invalid || this.loading()) {
      return;
    }

    this.loading.set(true);
    this.errorMessage.set('');
    this.cities.set([]);

    try {
      const { cities } = await this._getCitiesService.handle({
        email: this.emailFormControl.value as string,
      });

      if (!cities.length) {
        this.errorMessage.set(
          'Não encontramos uma conta para este e-mail. Verifique o endereço informado.'
        );
        return;
      }

      if (cities.length === 1) {
        this.goToLogin(cities[0].identity);
        return;
      }

      this.cities.set(cities);
    } catch {
      this.errorMessage.set('Não foi possível verificar o e-mail. Tente novamente em instantes.');
    } finally {
      this.loading.set(false);
    }
  }

  selectCity(): void {
    if (this.cityFormControl.value) {
      this.goToLogin(this.cityFormControl.value);
    }
  }

  private goToLogin(cityIdentity: string): void {
    const redirectTo = this.redirectToParam();
    const returnTo = this.returnToParam();

    if (!redirectTo || !returnTo) {
      this.errorMessage.set('Sessão de login inválida. Reinicie o acesso pelo aplicativo.');
      return;
    }

    // redirectTo (=/oauth/session) + returnTo seguem como query params; o login-by-form
    // os propaga até o postAuth, que anexa ?code= ao redirectTo no fim do login.
    this._router.navigate([`/${cityIdentity}`], {
      queryParams: { redirectTo, returnTo },
    });
  }
}
